import styled from "styled-components";
import { BreadcrumbsCommon, ClipLoaderCommon, StyledButton } from "@components";
import { AppModuleLayout } from "@layouts";
import { PATH_COMMISSION_AGENT, PATH_HOME, PATH_PRIVACY_POLICY, PATH_TERM } from "@routes";
import { breakpoint, theme } from "@utilities";
import { useEffect, useState } from "react";
import { pointsApi } from "@api";
import {
    getMetaData,
    initializeAccountDetail,
    selectAccount,
    useAppDispatch,
    useAppSelector,
} from "@redux";
import { useNavigate } from "react-router-dom";
import { IPointsData } from "@interfaces";
import { ErrorToast } from "src/Components/toasts/ErrorToast";
import { Dropdown } from "src/Module/CustomDropdown";

interface AgentFormData {
    bankBsb: string;
    bankAccount: string;
    bankCountryShortCode: string;
    businessNumber: string;
    tradingName: string;
}

interface FormErrors {
    bankBsb?: string;
    bankAccount?: string;
    bankCountryShortCode?: string;
    businessNumber?: string;
    tradingName?: string;
}

const validateForm = (data: AgentFormData): FormErrors => {
    const errors: FormErrors = {};

    if (!data.bankBsb.trim()) errors.bankBsb = "BSB is required";
    else if (!/^\d{6}$/.test(data.bankBsb)) errors.bankBsb = "BSB must be 6 digits";

    if (!data.bankAccount.trim()) errors.bankAccount = "Account number is required";
    else if (!/^\d{6,10}$/.test(data.bankAccount)) errors.bankAccount = "Invalid account number";

    if (!data.bankCountryShortCode) errors.bankCountryShortCode = "Country is required";

    if (!data.businessNumber.trim()) errors.businessNumber = "Business number is required";

    if (!data.tradingName.trim()) errors.tradingName = "Trading name is required";

    return errors;
};

const COUNTRY_OPTIONS = [
    { label: "Australia", value: "au" },
    { label: "New Zealand", value: "nz" },
    { label: "United States", value: "us" },
];

const BENEFITS = [
    "Commission earnings for each booking",
    "Real-time progress tracking",
    "Requires bank details and ABN/NZBN",
    "Access to exclusive deals & agent tools",
    "Refer others & earn trailing commissions",
];

const INITIAL_POINTS_DATA: IPointsData = {
    subSystemType: "",
    pointsBalance: 0,
    availableBalance: 0,
    pointsMultiplier: 0,
};

const INITIAL_FORM_DATA: AgentFormData = {
    bankBsb: "",
    bankAccount: "",
    bankCountryShortCode: "",
    businessNumber: "",
    tradingName: "",
};

const UpgradePopup = ({
    onConfirm,
    onCancel,
}: {
    onConfirm: (event: React.MouseEvent<HTMLButtonElement>) => void;
    onCancel: () => void;
}) => (
    <PopupOverlay onClick={onCancel}>
        <PopupContent onClick={(e) => e.stopPropagation()}>
            <h3>Upgrade Account?</h3>
            <p>Are you sure you want to upgrade your account to commission agent?</p>
            <PopupActions>
                <PopupButton $primary onClick={onConfirm}>
                    Yes, Upgrade
                </PopupButton>
                <PopupButton onClick={onCancel}>Cancel</PopupButton>
            </PopupActions>
        </PopupContent>
    </PopupOverlay>
);

const AgentForm = ({
    formData,
    onChange,
    errors,
}: {
    formData: AgentFormData;
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
    errors: FormErrors;
}) => (
    <AgentFormContainer>
        <StyledAgentForm>
            <h3>Add Agent/Bank Details</h3>
            <p>Update your Banking information and your business details.</p>
            <input
                type="text"
                name="bankBsb"
                placeholder="Bank BSB"
                value={formData.bankBsb}
                onChange={onChange}
                style={{ borderColor: errors.bankBsb ? "#ff4444" : "#ddd" }}
            />
            {errors.bankBsb && <ErrorText>{errors.bankBsb}</ErrorText>}
            <input
                type="text"
                name="bankAccount"
                placeholder="Bank Account Number"
                value={formData.bankAccount}
                onChange={onChange}
                style={{ borderColor: errors.bankAccount ? "#ff4444" : "#ddd" }}
            />
            {errors.bankAccount && <ErrorText>{errors.bankAccount}</ErrorText>}
            <Dropdown
                options={COUNTRY_OPTIONS}
                value={formData.bankCountryShortCode}
                onChange={(value) =>
                    onChange({ target: { name: "bankCountryShortCode", value } } as any)
                }
                placeholder="Country"
            />
            {errors.bankCountryShortCode && <ErrorText>{errors.bankCountryShortCode}</ErrorText>}
            <input
                type="text"
                name="businessNumber"
                placeholder="Business Number"
                value={formData.businessNumber}
                onChange={onChange}
                style={{ borderColor: errors.businessNumber ? "#ff4444" : "#ddd" }}
            />
            {errors.businessNumber && <ErrorText>{errors.businessNumber}</ErrorText>}
            <input
                type="text"
                name="tradingName"
                placeholder="Trading Name"
                value={formData.tradingName}
                onChange={onChange}
                style={{ borderColor: errors.tradingName ? "#ff4444" : "#ddd" }}
            />
            {errors.tradingName && <ErrorText>{errors.tradingName}</ErrorText>}
        </StyledAgentForm>
    </AgentFormContainer>
);

const SuccessView = ({
    account,
    commissionDisplay,
    onBackHome,
}: {
    account: any;
    commissionDisplay: string;
    onBackHome: () => void;
}) => (
    <>
        <div className="agentAccount">
            <StyledSuccessContainer>
                <img src="/images/LargeTickMark.svg" alt="LargeTickMark" />
                <StyledSuccessHeading>
                    Upgraded to Commission Agent Successfully.
                </StyledSuccessHeading>
                <StyledSuccessMessage>
                    You've been successfully upgraded to a Commission Agent! Now you can start
                    exploring and earning Commissions for every trip you make, and redeem directly
                    into your bank account.
                </StyledSuccessMessage>
                <StyledCenteredDiv>
                    <h6>Agent Name</h6>
                    <p>
                        {account?.first_name} {account?.last_name}
                    </p>
                </StyledCenteredDiv>
                <StyledCenteredDiv>
                    <h6>Commission Balance</h6>
                    <h2>{commissionDisplay}</h2>
                </StyledCenteredDiv>
            </StyledSuccessContainer>
        </div>
        <StyledFooter>
            <StyledButtonUpgrade
                $borderRadius={theme.borderRadius.small}
                color={theme.gradients.button}
                onClick={onBackHome}
            >
                Back to Homepage
            </StyledButtonUpgrade>
        </StyledFooter>
    </>
);

const UpgradeView = ({
    showUpgradePage,
    isUpgrading,
    onUpgrade,
}: {
    showUpgradePage: boolean;
    isUpgrading: boolean;
    onUpgrade: () => void;
}) => (
    <>
        {!showUpgradePage && (
            <div className="agentAccount">
                <AgentInfoContainer>
                    <AgentInfo>
                        <h3>Become a Commission Agent</h3>
                        <p>
                            Earn real cash commissions on every booking as a verified freelance
                            agent.
                        </p>
                        <h4>What you'll get</h4>
                        <Divider />
                        {BENEFITS.map((benefit, index) => (
                            <InfoContainer key={index}>
                                <img src="/images/TickMark.svg" alt="check" />
                                <Info>{benefit}</Info>
                            </InfoContainer>
                        ))}
                    </AgentInfo>
                </AgentInfoContainer>
            </div>
        )}
        <Footer>
            {isUpgrading ? (
                <StyledButtonUpgrade $borderRadius={theme.borderRadius.small} disabled>
                    <ClipLoaderCommon size={24} color={theme.colors.main} margin={10} />
                </StyledButtonUpgrade>
            ) : (
                <StyledButtonUpgrade
                    $borderRadius={theme.borderRadius.small}
                    color={theme.gradients.button}
                    onClick={onUpgrade}
                >
                    Upgrade to Commission Agent
                </StyledButtonUpgrade>
            )}
            <TermsAndCondition>
                By continuing, you agree to our <a href={PATH_TERM}>Terms of Service</a> and{" "}
                <a href={PATH_PRIVACY_POLICY}>Privacy Policy</a>
            </TermsAndCondition>
        </Footer>
    </>
);

export const CommissionAgent = () => {
    const [showUpgradePage, setShowUpgradePage] = useState(false);
    const [showUpgradePopup, setShowUpgradePopup] = useState(false);
    const [isUpgrading, setIsUpgrading] = useState(false);
    const [metadataLoaded, setMetadataLoaded] = useState(false);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { account, metadata } = useAppSelector(selectAccount);
    const [pointsData, setPointsData] = useState<IPointsData>(INITIAL_POINTS_DATA);
    const [formData, setFormData] = useState<AgentFormData>(INITIAL_FORM_DATA);
    const [formErrors, setFormErrors] = useState<FormErrors>({});

    const breadcrumbsData = [
        { title: "Home", link: PATH_HOME },
        { title: "Upgrade", link: PATH_COMMISSION_AGENT },
    ];

    const handleActualUpgrade = async (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ): Promise<void> => {
        event.preventDefault();
        setIsUpgrading(true);
        setShowUpgradePopup(false);

        try {
            const res = await pointsApi.upgradeToCommissionAgent(formData);
            if (res.data.success) {
                await dispatch(initializeAccountDetail());
                await dispatch(getMetaData());
            }
        } catch (error: any) {
            if (error?.response?.data?.title) {
                ErrorToast({ Message: error.response.data.title });
            }
        } finally {
            setIsUpgrading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleUpgradeClick = () => {
        if (!showUpgradePage) {
            setShowUpgradePage(true);
        } else {
            const errors = validateForm(formData);
            setFormErrors(errors);
            if (Object.keys(errors).length === 0) {
                setShowUpgradePopup(true);
            } else {
                ErrorToast({ Message: "Please fill in all required fields correctly" });
            }
        }
    };

    const getCommissionDisplay = () => {
        if (metadata?.is_commission_agent && pointsData.pointsMultiplier > 0) {
            const dollarAmount = pointsData.availableBalance / pointsData.pointsMultiplier;
            return `$${dollarAmount.toFixed(2)}`;
        }
        return `${pointsData.availableBalance} Points`;
    };

    useEffect(() => {
        document.body.style.overflow = showUpgradePopup ? "hidden" : "auto";
        return () => {
            document.body.style.overflow = "auto";
        };
    }, [showUpgradePopup]);

    useEffect(() => {
        dispatch(getMetaData()).then((result: any) => {
            console.log("[CommissionAgent] metadata result:", JSON.stringify(result?.payload));
            setMetadataLoaded(true);
        });
    }, [dispatch]);

    useEffect(() => {
        if (!account?.is_agent_integrated) return;
        pointsApi.getPointsData().then((res) => {
            setPointsData(res.data.data);
        });
    }, [account?.is_agent_integrated]);

    useEffect(() => {
        if (metadata?.is_commission_agent) {
            setShowUpgradePage(false);
        }
    }, [metadata?.is_commission_agent]);

    return (
        <AppModuleLayout>
            <StyledUpgradeAgentPage>
                {showUpgradePopup && showUpgradePage && (
                    <UpgradePopup
                        onConfirm={handleActualUpgrade}
                        onCancel={() => setShowUpgradePopup(false)}
                    />
                )}
                <div className="wrapper">
                    <div className="breadCrumbs">
                        <BreadcrumbsCommon data={breadcrumbsData} />
                    </div>
                    <p className="heading">Upgrade Your Account</p>
                    <div className="subWrapper">
                        <ContainerBody>
                            {showUpgradePage && (
                                <AgentForm
                                    formData={formData}
                                    onChange={handleInputChange}
                                    errors={formErrors}
                                />
                            )}
                            {!metadataLoaded ? (
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        padding: "50px",
                                    }}
                                >
                                    <ClipLoaderCommon
                                        size={50}
                                        color={theme.colors.main}
                                        margin={10}
                                    />
                                </div>
                            ) : metadata?.is_commission_agent ? (
                                <SuccessView
                                    account={account}
                                    commissionDisplay={getCommissionDisplay()}
                                    onBackHome={() => navigate(PATH_HOME)}
                                />
                            ) : (
                                <UpgradeView
                                    showUpgradePage={showUpgradePage}
                                    isUpgrading={isUpgrading}
                                    onUpgrade={handleUpgradeClick}
                                />
                            )}
                        </ContainerBody>
                    </div>
                </div>
            </StyledUpgradeAgentPage>
        </AppModuleLayout>
    );
};

const StyledFooter = styled.div`
    position: fixed;
    bottom: 0;
    width: 100%;
    padding: 16px;
    display: flex;
    justify-content: center;
    align-items: center;

    ${breakpoint.breakMedium`
        position: static;
        padding: 24px;
    `}

    ${breakpoint.breakMedium`
        position: static;
        padding: 24px;
        margin-bottom : 104px;
    `}
`;

const StyledSuccessContainer = styled.div`
    font-family: Poppins;
    line-height: 120%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 104px 24px 0px 24px;
    gap: 24px;

    ${breakpoint.breakOnlyMobile`
        background-color: #fff;
        flex: 1;
        border-radius: 8px;
        min-height: calc(88vh - 200px);
        justify-content: center;
        align-items: center;;
        padding: 24px 24px;
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
    `}
`;

const StyledSuccessHeading = styled.h2`
    font-family: "Plus Jakarta Sans";
    text-align: center;
    font-weight: 700;
    font-size: 18px;
`;

const StyledSuccessMessage = styled.p`
    text-align: center;
    color: #6e7375;
    line-height: 120%;
`;

const StyledCenteredDiv = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    text-align: center;
    h6 {
        font-weight: 400;
    }
    h2 {
        font-weight: 600;
        color: ${theme.colors.highlight};
        font-size: 24px;
    }
    p {
        font-weight: 500;
        color: ${theme.colors.highlight};
        font-size: 18px;
    }
`;

const StyledUpgradeAgentPage = styled.div`
    padding: 16px;
    background-color: #f2f2f2;
    flex: 1;

    .agentAccount {
        flex: 1;
        ${breakpoint.breakTablet`
            margin-top: 0;
        `}
    }

    .wrapper {
        margin: 0 auto;
        .heading {
            font-size: 18px;
            font-family:
                Plus Jakarta Sans,
                sans-serif;
            font-weight: 700;
            line-height: 120%;
            margin-top: 16px;
            margin-bottom: 15px;
            display: flex;
            gap: 30px;

            ${breakpoint.breakIpadPro`
                font-size: 28px;
                margin-bottom: 30px;
                margin-top: 24px;
            `}
        }

        ${breakpoint.breakTablet`
            width: ${(p) => p.theme.widths.wide_700};
        `}

        ${breakpoint.breakIpadPro`
            width: ${(p) => p.theme.widths.wide_900};
        `}

        ${breakpoint.breakLarge`
            width: ${(p) => p.theme.widths.wide_1110};
        `}
    }

    .menu {
        display: none;

        ${breakpoint.breakTablet`
            display: block;
        `}
    }

    .breadCrumbs {
        ${breakpoint.breakTablet`
            margin-top: 14px;
        `}
        ${breakpoint.breakOnlyMobile`
            left: 16px;
        `}
    }

    .subWrapper {
        ${breakpoint.breakTablet`
            display: flex;
            flex-direction: column;
            margin-top: 30px;
            justify-content: center;
            align-items: center;
        `}
    }
`;

const ContainerBody = styled.div`
    background-color: #fff;
    align-items: center;
    justify-content: center;
    border-radius: 16px;
    margin-bottom: 112px;
    width: 736px;
    box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.1);

    ${breakpoint.breakOnlyMobile`
        background-color: transparent;
        display: flex;
        flex-direction: column;
        margin-bottom: 0px;
        justify-content: start;
        width: 100%;
        box-shadow: none;
    `}
`;

const AgentInfoContainer = styled.div`
    font-family: Poppins, sans-serif;
    line-height: 120%;
    display: flex;
    justify-content: center;
    align-items: center;

    ${breakpoint.breakOnlyMobile`
        flex: 1;
        border-radius: 8px;
        min-height: calc(88vh - 200px);
        justify-content: start;
        align-items: start;
    `}
`;

const AgentInfo = styled.div`
    background-color: #fff;
    padding: 32px 0px 0px 0px;
    border-radius: 8px;
    width: 380px;

    ${breakpoint.breakOnlyMobile`
        padding: 16px;
        width: 100%;
    `}

    h3 {
        font-weight: 600;
        font-size: 18px;
        color: ${theme.colors.red};
    }

    p {
        font-size: 14px;
        color: #6e7375;
        margin-top: 8px;
        line-height: 150%;
    }

    h4 {
        font-size: 16px;
        font-weight: 600;
        color: #131214;
        margin-top: 12px;
    }
`;

const Divider = styled.div`
    margin: 16px 0 16px 0;
    border-top: 1px solid;
    border-color: #e6e9eb;
    ${breakpoint.breakOnlyMobile`
        margin: 12px 0 12px 0;
    `}
`;

const InfoContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    margin-top: 12px;
`;

const Info = styled.p`
    font-size: 14px;
    margin: 0px !important;
    color: #131214 !important;
`;

const AgentFormContainer = styled.div`
    font-family: Poppins, sans-serif;
    line-height: 120%;
    display: flex;
    justify-content: center;
    align-items: center;

    ${breakpoint.breakOnlyMobile`
        flex: 1;
        border-radius: 8px;
        min-height: calc(88vh - 200px);
        justify-content: start;
        align-items: start;
    `}
`;

const StyledAgentForm = styled.form`
    background-color: #fff;
    padding: 32px 0px 0px 0px;
    border-radius: 8px;
    width: 380px;
    display: flex;
    flex-direction: column;
    gap: 12px;

    ${breakpoint.breakOnlyMobile`
        padding: 16px;
        width: 100%;
    `}

    h3 {
        font-size: 16px;
        font-weight: 600;
        margin-bottom: 4px;
    }

    p {
        font-size: 14px;
        color: #6e7375;
        margin-bottom: 12px;
    }

    input,
    select {
        border: 1px solid #ddd;
        border-radius: 8px;
        padding: 16px;
        font-size: 16px;
        outline: none;
        transition: border 0.2s ease;
        background-color: #ffffff;
        color: #6e7375;
    }
`;

const Footer = styled.div`
    display: flex;
    gap: 24px;
    flex: 1;
    font-family: Poppins, sans-serif;
    flex-direction: column;
    align-items: center;
    padding: 20px 0;

    ${breakpoint.breakOnlyMobile`
        position: sticky;
        bottom: 0;
        background: #f2f2f2;
    `}
`;

const StyledButtonUpgrade = styled(StyledButton)`
    margin-top: 0px;
    width: 100%;
    max-width: 380px;
    font-weight: 500;
    ${breakpoint.breakOnlyMobile`
            max-width: none;
    `}
`;

const TermsAndCondition = styled.p`
    font-size: 12px;
    color: #6e7375;
    text-align: center;
    width: 360px;
    line-height: 150%;
    /* Matches the same sentence on the Link Agent page. */
    a {
        color: #747474;
        text-decoration: underline;
    }
    ${breakpoint.breakOnlyMobile`
        width:100%
    `}
`;

const PopupOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10;
`;

const PopupContent = styled.div`
    background: white;
    padding: 2rem;
    border-radius: 8px;
    max-width: 400px;
    text-align: center;

    ${breakpoint.breakOnlyMobile`
        max-width: 350px;
    `}

    h3 {
        margin-bottom: 1rem;
        font-size: 24px;
    }

    p {
        margin-bottom: 2rem;
        color: #6e7375;
    }
`;

const PopupActions = styled.div`
    display: flex;
    gap: 1rem;
    justify-content: center;
    flex-direction: column;
`;

const PopupButton = styled.button<{ $primary?: boolean }>`
    padding: 8px 24px;
    border-radius: 4px;
    height: 48px;
    border: ${({ $primary }) => ($primary ? "none" : `2px solid ${theme.colors.main}`)};
    background: ${({ $primary }) =>
        $primary
            ? `linear-gradient(90deg, ${theme.gradients.button[0]}, ${theme.gradients.button[1]})`
            : "#fff"};
    color: ${({ $primary }) => ($primary ? theme.colors.button_text : theme.colors.button_text1)};
    cursor: pointer;
    font-size: 16px;
    font-weight: 500;
`;

const ErrorText = styled.span`
    color: #ff4444;
    font-size: 12px;
    margin-top: -8px;
`;
